import { jsx as _jsx } from "react/jsx-runtime";
import { useEffect, useMemo } from 'react';
import { PeriodDateRange, getPeriodDateRangeValues, periodDateRangeDeserializer, } from '../PeriodDateRange';
import { convertToDateRange, getDatesFromStoredValue, prepareForStorage } from './helpers';
import { useCoreSessionStorage } from '@/core/hooks';
import { useDebouncedCallback } from 'use-debounce';
const debouncedDateRangeValueDelay = 900;
/**
 * Enhances {@link PeriodDateRange} component.
 *
 * Addons: stores value in *sessionStorage*, adds debounce on value change.
 *
 * **Important**: should be used with explicit generic type so that `sessionStorageKey` becomes app literal type.
 * Also, it should be used as base component for inherited Named PeriodDateRange component.
 *
 * @param {string} sessionStorageKey - required, key under which the value will be stored.
 * @param {boolean} [disabled] - optional
 * @param {string} [buttonClassName] - optional, className to be applied to all toggle buttons.
 * @param {(value: DateRangeFromTo) => void} onChange
 * @returns
 */
const StoredPeriodDateRange = ({ sessionStorageKey, defaultPeriod = 'month', disabled, buttonClassName, onChange, ...dateRangePickerOwnProps }) => {
    const initialValue = useMemo(() => ({
        period: defaultPeriod,
        // // last two weeks for `custom` range
        // periodDateFrom: customPeriodDefaultDateFrom,
        // periodDateTo: customPeriodDefaultDateTo,
        ...getPeriodDateRangeValues(defaultPeriod),
    }), [defaultPeriod]);
    const [storageValue, setStorageValue] = useCoreSessionStorage(sessionStorageKey, initialValue, {
        deserializer: periodDateRangeDeserializer,
    });
    // TODO: return after usehooks-ts will have fixed not working hook
    // const onChangeDebounced = useDebounceCallback(onChange, debouncedDateRangeValueDelay);
    const onChangeDebounced = useDebouncedCallback(onChange, debouncedDateRangeValueDelay);
    useEffect(() => {
        // Force value update to have stored or initial value to pass to a query that handles onChange event (since value is
        // null without that).
        onChange(getDatesFromStoredValue(storageValue));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);
    return (_jsx(PeriodDateRange, { value: storageValue, disabled: disabled, invalid: false, buttonClassName: buttonClassName, onChange: (value) => {
            setStorageValue(prepareForStorage(value));
            onChangeDebounced(convertToDateRange(value));
        }, ...dateRangePickerOwnProps }));
};
export { StoredPeriodDateRange };
